import { useState, useEffect } from 'react';

interface UseAnimatedCounterOptions {
  end: number;
  duration?: number;
  delay?: number;
  decimals?: number;
}

export const useAnimatedCounter = ({ 
  end, 
  duration = 1000, 
  delay = 0,
  decimals = 0 
}: UseAnimatedCounterOptions) => {
  const [count, setCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (end === 0) {
      setCount(0);
      return;
    }

    const timer = setTimeout(() => {
      setIsAnimating(true);
      const startTime = Date.now();
      const startValue = 0;
      const endValue = end;

      const animate = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        
        const currentValue = startValue + (endValue - startValue) * easeOutQuart;
        setCount(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };

      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timer);
  }, [end, duration, delay]);

  const formattedValue = decimals > 0 
    ? count.toFixed(decimals)
    : Math.round(count).toString();

  return { value: formattedValue, isAnimating };
};